/**
 * OPS-GOVERNANCE-UAT-002 — filter Matx / healthcare-ui navigation by operational grants.
 * Menu items from auth principal are provisioned separately; visibility must follow
 * resolveOperationalAccess (permissions, uriList, ACL, serverModules) — not raw menu tree.
 *
 * Workflow-gated desks (e.g. Pharmacy Procurement) require matching JWT workflow keys so
 * URI-only grants cannot open a page that immediately 403s on assertPharmacyProcure.
 */
import { resolveModuleIdForPath } from './resolveOperationalAccess.js';

function canSeeMenuPath(path, access) {
  if (!path) return true;
  const moduleId = resolveModuleIdForPath(path);
  if (moduleId) return access.canAccessModule(moduleId);
  return access.canAccessPath(path);
}

function filterChildren(children, access) {
  if (!Array.isArray(children)) return [];
  return children.filter((child) => {
    if (!child?.path) return true;
    return canSeeMenuPath(child.path, access);
  });
}

/**
 * @param {Array} navItems output of convertMenuToNavigation
 * @param {{ canAccessPath: (path: string) => boolean }} access
 * @returns {Array}
 */
export function filterNavigationByAccess(navItems, access) {
  if (!access?.canAccessPath || !Array.isArray(navItems)) {
    return navItems ?? [];
  }

  const filtered = [];
  let pendingLabel = null;

  const flushLabel = () => {
    if (pendingLabel) {
      filtered.push(pendingLabel);
      pendingLabel = null;
    }
  };

  for (const item of navItems) {
    if (item?.type === "label") {
      pendingLabel = item;
      continue;
    }

    if (Array.isArray(item?.children) && item.children.length > 0) {
      const children = filterChildren(item.children, access);
      if (!children.length) continue;
      flushLabel();
      filtered.push({ ...item, children });
      continue;
    }

    if (item?.path && !canSeeMenuPath(item.path, access)) {
      continue;
    }

    flushLabel();
    filtered.push(item);
  }

  return filtered;
}
